import { NotificationCategory } from '@msrllabs/pt-api/types';
import type { TFunction } from 'i18next';

import { AppTheme } from '@/Theme';

import { NotificationFilterCategory } from './NotificationConstants';

const TradingIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'>
    <path d='M3 17 9 11l4 4 8-8M14 7h7v7' />
  </svg>
);

const SocialIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'>
    <path d='M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21.3l8.8-8.8a5.5 5.5 0 0 0 0-7.8Z' />
  </svg>
);

const AccountIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'>
    <circle cx='12' cy='8' r='4' />
    <path d='M4 21c0-4 4-7 8-7s8 3 8 7' />
  </svg>
);

const OthersIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 8v4M12 16v.01' />
  </svg>
);

/** Display label for a category; literal keys so the extractor sees them. */
export const notificationCategoryLabel = (category: NotificationFilterCategory | undefined, t: TFunction): string => {
  switch (category) {
    case 'all':
      return t('all');
    case 'trading':
      return t('trading');
    case 'social':
      return t('social');
    case 'account':
      return t('account');
    default:
      return t('others');
  }
};

/** Accent color for a notification category (dots, icon tints). Brand semantic hues
 *  that read in both modes: trading = buy-green, social = teal, account = cobalt,
 *  all = neutral. */
export const notificationCategoryColor = (category: NotificationFilterCategory | undefined, theme: AppTheme): string => {
  switch (category) {
    case 'all':
      return theme.brand.textSecondary;
    case 'trading':
      return theme.brand.buyGreen;
    case 'social':
      return theme.brand.accentCyan;
    case 'account':
      return theme.brand.accentInk;
    default:
      return theme.brand.textTertiary;
  }
};

/** Line-art glyph for a notification category. Sized by the parent (CSS on svg). */
export const NotificationCategoryIcon: React.FC<{ category: NotificationCategory | undefined }> = ({ category }) => {
  switch (category) {
    case 'trading':
      return <TradingIcon />;
    case 'social':
      return <SocialIcon />;
    case 'account':
      return <AccountIcon />;
    default:
      return <OthersIcon />;
  }
};
